import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import PageLayout from '../components/PageLayout';
import LoggedInHome from '../components/LoggedInHome';
import AuthModal from '../components/AuthModal';
import QuizCTABanner from '../components/QuizCTABanner';
import axios from 'axios';
import { toast } from 'sonner';
import { Plane, MapPin, Users, MessageSquare, ArrowRight, Star, Zap, Shield, Globe, ChevronRight, TrendingUp, Route, Search, Clock, Navigation } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import SEOHead from '../components/SEOHead';

import { API } from '../config';

// ─── Animated heading phrases ─────────────────────────────────────────────────
const HEADING_PHRASES = [
  { line1: 'Your flights,',    accent: 'your community' },
  { line1: 'Every route,',     accent: 'a story to share' },
  { line1: 'Travel smarter,',  accent: 'together' },
  { line1: 'Real tips from',   accent: 'real travelers' },
  { line1: 'Connect at',       accent: '35,000 feet' },
  { line1: 'One app for',      accent: 'every journey' },
];

// ─── Hero route preview cards ─────────────────────────────────────────────────
const ROUTE_PREVIEWS = [
  {
    route: 'DXB → LHR', airline: 'Emirates EK101', experiences: 312, badge: 'Active',
    posts: [
      { name: 'Ahmed K.',  type: 'Seat Tip',   msg: 'Row 41A has the best window view. Highly recommend!',             icon: '💺', delay: 0 },
      { name: 'Sarah Mo.', type: 'Experience', msg: 'Business class food was exceptional. Mezze to start, then lamb.', icon: '⭐', delay: 0.6 },
      { name: 'Omar Ta.',  type: 'Tip',        msg: 'Fast track security at T3 saves 40 mins — worth it!',             icon: '💡', delay: 1.2 },
    ],
  },
  {
    route: 'RUH → LHR', airline: 'Saudia SV001', experiences: 186, badge: 'Popular',
    posts: [
      { name: 'Khalid A.', type: 'Experience', msg: 'Sky Lounge business class — flat bed was perfect for this route.', icon: '⭐', delay: 0 },
      { name: 'Fatima H.', type: 'Lounge Tip', msg: 'Al-Fursan lounge in RUH has amazing kabsa. Worth arriving early!',  icon: '🍽️', delay: 0.6 },
      { name: 'Omar S.',   type: 'Seat Tip',   msg: 'Seat 1A is the most private on this aircraft. Book it!',           icon: '💺', delay: 1.2 },
    ],
  },
  {
    route: 'DOH → JFK', airline: 'Qatar Airways QR701', experiences: 245, badge: 'Trending',
    posts: [
      { name: 'Tariq M.',  type: 'Experience', msg: 'QSuite is absolutely worth the upgrade. Best flight of my life.', icon: '⭐', delay: 0 },
      { name: 'Maria J.',  type: 'Tip',        msg: 'US Pre-clearance at DOH means you land at JFK as domestic!',      icon: '💡', delay: 0.6 },
      { name: 'James P.',  type: 'Seat Tip',   msg: 'Double QSuite with my partner — private room in the sky.',        icon: '💺', delay: 1.2 },
    ],
  },
  {
    route: 'SIN → SYD', airline: 'Singapore Airlines SQ231', experiences: 178, badge: 'Active',
    posts: [
      { name: 'Lucy C.',   type: 'Experience', msg: 'SilverKris lounge noodle bar — the laksa was incredible!',        icon: '⭐', delay: 0 },
      { name: 'Yuki T.',   type: 'Tip',        msg: 'Changi Jewel waterfall light show at 7:30 PM is magical.',        icon: '💡', delay: 0.6 },
      { name: 'Priya S.',  type: 'Seat Tip',   msg: 'Suites class seat 1A has a full closing door. Pure privacy.',     icon: '💺', delay: 1.2 },
    ],
  },
  {
    route: 'IST → JFK', airline: 'Turkish Airlines TK1', experiences: 154, badge: 'Popular',
    posts: [
      { name: 'Elena P.',  type: 'Lounge Tip', msg: 'CIP Lounge in IST has a CINEMA. Plan a 3-hour connection!',       icon: '🎬', delay: 0 },
      { name: 'Carlos M.', type: 'Experience', msg: 'Turkish mezze in Business is the best airline food, period.',     icon: '⭐', delay: 0.6 },
      { name: 'Nora A.',   type: 'Seat Tip',   msg: 'Window seats 4A/K on 777 are the most private in Business.',     icon: '💺', delay: 1.2 },
    ],
  },
];

const POST_TYPE_STYLES = {
  gate_change: 'chip-gate', crowd_level: 'chip-crowd', food_rec: 'chip-food',
  delay_alert: 'chip-delay', lounge_tip: 'chip-lounge', general: 'chip-crowd',
};
const POST_TYPE_LABELS = {
  gate_change: 'Gate Change', crowd_level: 'Crowd Alert', food_rec: 'Food Tip',
  delay_alert: 'Delay Alert', lounge_tip: 'Lounge Tip', general: 'Update',
};

// ─── AnimatedHeading ─────────────────────────────────────────────────────────
function AnimatedHeading() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIdx(i => (i + 1) % HEADING_PHRASES.length);
    }, 3200);
    return () => clearInterval(timer);
  }, []);

  const { line1, accent } = HEADING_PHRASES[idx];

  return (
    <h1
      className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-4"
      style={{ fontFamily: 'Space Grotesk' }}
    >
      <AnimatePresence mode="wait">
        <motion.span
          key={`line1-${idx}`}
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.38, ease: 'easeOut' }}
          className="block"
        >
          {line1}
        </motion.span>
      </AnimatePresence>
      <AnimatePresence mode="wait">
        <motion.span
          key={`accent-${idx}`}
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.38, delay: 0.08, ease: 'easeOut' }}
          className="block text-[#FF4500]"
        >
          {accent}
        </motion.span>
      </AnimatePresence>
    </h1>
  );
}

// ─── AnimatedRouteCard ────────────────────────────────────────────────────────
function AnimatedRouteCard() {
  const [routeIdx, setRouteIdx] = useState(0);
  const [visiblePosts, setVisiblePosts] = useState([]);
  const timerRef = useRef(null);

  const currentRoute = ROUTE_PREVIEWS[routeIdx];

  // On route change, progressively reveal posts
  useEffect(() => {
    setVisiblePosts([]);
    if (timerRef.current) clearTimeout(timerRef.current);

    const delays = [0, 650, 1300];
    delays.forEach((d, i) => {
      setTimeout(() => {
        setVisiblePosts(prev => {
          if (prev.length <= i) return [...prev, currentRoute.posts[i]];
          return prev;
        });
      }, d);
    });

    // Advance to next route after 5s
    timerRef.current = setTimeout(() => {
      setRouteIdx(idx => (idx + 1) % ROUTE_PREVIEWS.length);
    }, 5200);

    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [routeIdx]);  // eslint-disable-line

  const badgeColor = {
    Active: 'status-on-time', Popular: 'status-boarding', Trending: 'bg-violet-100 text-violet-700 border border-violet-200',
  };

  return (
    <div className="relative">
      {/* Floating stats badge */}
      <motion.div
        animate={{ y: [0, -4, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute -top-4 -right-4 bg-[#10B981] text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg z-10 flex items-center gap-1.5"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
        Live community
      </motion.div>

      {/* Route indicator dots */}
      <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
        {ROUTE_PREVIEWS.map((_, i) => (
          <button
            key={i}
            onClick={() => setRouteIdx(i)}
            className={`w-1.5 h-1.5 rounded-full transition-all ${i === routeIdx ? 'bg-[#FF4500] w-4' : 'bg-border'}`}
          />
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={`route-card-${routeIdx}`}
          initial={{ opacity: 0, x: 30, scale: 0.97 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: -30, scale: 0.97 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
          className="fly-card p-4 max-w-sm mx-auto"
        >
          {/* Card header */}
          <div className="flex items-center gap-2 mb-3 pb-3 border-b border-border">
            <div className="w-9 h-9 rounded-xl bg-[#FF4500]/15 flex items-center justify-center">
              <Plane className="w-4 h-4 text-[#FF4500]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold">{currentRoute.route}</div>
              <div className="text-xs text-muted-foreground truncate">{currentRoute.airline} · {currentRoute.experiences} experiences</div>
            </div>
            <Badge className={`text-[10px] shrink-0 ${badgeColor[currentRoute.badge] || 'status-on-time'}`}>
              {currentRoute.badge}
            </Badge>
          </div>

          {/* Animated posts */}
          <div className="space-y-3 min-h-[140px]">
            <AnimatePresence>
              {visiblePosts.map((post, i) => (
                <motion.div
                  key={`${routeIdx}-${i}`}
                  initial={{ opacity: 0, y: 10, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  transition={{ duration: 0.35, ease: 'easeOut' }}
                  className="flex gap-2.5"
                >
                  <img
                    src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${post.name}`}
                    alt=""
                    className="w-7 h-7 rounded-full flex-shrink-0 mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <span className="text-[10px] font-semibold text-foreground">{post.name}</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground shrink-0">
                        {post.icon} {post.type}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{post.msg}</p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Typing indicator when waiting for next post */}
            {visiblePosts.length < 3 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex gap-2.5 items-center"
              >
                <div className="w-7 h-7 rounded-full bg-muted flex-shrink-0" />
                <div className="flex gap-1 py-2">
                  {[0, 0.15, 0.3].map((d, i) => (
                    <motion.span
                      key={i}
                      animate={{ y: [0, -4, 0] }}
                      transition={{ duration: 0.6, repeat: Infinity, delay: d }}
                      className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40"
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// ─── Main Landing Component ───────────────────────────────────────────────────
export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [airports, setAirports] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [trendingRoutes, setTrendingRoutes] = useState([]);
  const [flightNumber, setFlightNumber] = useState('');
  const [checkingIn, setCheckingIn] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Route finder state
  const [rfOrigin, setRfOrigin] = useState('');
  const [rfDest, setRfDest]     = useState('');
  const [rfLoading, setRfLoading] = useState(false);
  const [rfResult, setRfResult]   = useState(null);
  const [rfError, setRfError]     = useState('');

  const handleRouteLookup = async () => {
    const o = rfOrigin.trim().toUpperCase().slice(0, 3);
    const d = rfDest.trim().toUpperCase().slice(0, 3);
    if (o.length < 3 || d.length < 3) { toast.error('Enter 3-letter IATA codes for both airports'); return; }
    setRfLoading(true);
    setRfResult(null);
    setRfError('');
    try {
      const res = await axios.get(`${API}/routes/lookup?origin=${o}&destination=${d}`);
      setRfResult(res.data);
    } catch (e) {
      setRfError(e.response?.data?.detail || 'Route not found. Check the airport codes.');
    } finally {
      setRfLoading(false);
    }
  };

  const TESTIMONIALS = [
    { id: 1, name: 'Ahmad K.', role: 'Frequent Flyer', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=ahmadK', text: 'Found out about a gate change 20 minutes before the airline announced it. Fly N Out literally saved my flight!', rating: 5, airport: 'DXB' },
    { id: 2, name: 'Sara M.', role: 'Travel Blogger', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=saraM', text: 'The DXB-LHR route community had seat recommendations I could never have found elsewhere. Upgraded my whole experience.', rating: 5, airport: 'DOH' },
    { id: 3, name: 'Mohammed A.', role: 'Business Traveler', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=mohammedA', text: 'The EK101 route community was packed with reviews and seat tips. Made my trip so much better before I even boarded.', rating: 5, airport: 'RUH' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [airRes, discRes] = await Promise.all([
          axios.get(`${API}/airports`),
          axios.get(`${API}/discover`),
        ]);
        // Sort by post_count for most active airports
        const sorted = [...(airRes.data || [])].sort((a, b) => (b.post_count || 0) - (a.post_count || 0));
        setAirports(sorted.slice(0, 8));
        setRecentPosts(discRes.data.recent_posts?.slice(0, 4) || []);
        setTrendingRoutes(discRes.data.trending_routes?.slice(0, 6) || []);
      } catch (err) {
        console.error('Failed to load landing data', err);
      }
    };
    fetchData();
  }, []);

  const handleCheckin = async () => {
    if (!flightNumber.trim()) { toast.error('Enter a flight number'); return; }
    if (!user) { setShowAuth(true); setDrawerOpen(false); return; }
    setCheckingIn(true);
    try {
      const res = await axios.post(`${API}/flights/checkin`, { flight_number: flightNumber }, { withCredentials: true });
      toast.success(`Flight ${flightNumber.toUpperCase()} logged to your records!`);
      navigate(`/route/${res.data.route_id}`);
    } catch {
      toast.error('Check-in failed. Try again.');
    } finally {
      setCheckingIn(false);
    }
  };

  if (user) return (
    <PageLayout>
      <SEOHead
        title="Where Every Flight Becomes a Community"
        description="Log your flights, share route experiences, discover real-time airport tips, and join a worldwide community of travelers. Your journey starts here."
        keywords="flight community, airport tips, route experiences, travel social network"
      />
      <LoggedInHome />
      <AuthModal open={showAuth} onOpenChange={setShowAuth} />
    </PageLayout>
  );

  return (
    <PageLayout>
      <SEOHead
        title="Where Every Flight Becomes a Community"
        description="Fly N Out is the social network for airline travelers. Log flights, share route reviews, get real-time airport tips, and connect with flyers worldwide."
        keywords="flight community, airport chat, layover buddy, same flight chat, airport tips, travel social network, Fly N Out"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'TravelAgency',
          name: 'Fly N Out',
          url: 'https://flynout.com',
          description: 'Social community platform for airline travelers',
          priceRange: 'Free - SAR 29/month',
          areaServed: 'Worldwide',
        }}
      />

      {/* ── HERO ──────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden py-6 sm:py-10 fly-gradient-hero">
        <div
          className="absolute inset-0 opacity-10 dark:opacity-5"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1600&q=60')`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Left col */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1, duration: 0.4 }}
              >
                <Badge className="mb-3 bg-[#FF4500]/15 text-[#FF4500] border border-[#FF4500]/30 px-3 py-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FF4500] animate-pulse inline-block mr-2" />
                  Live right now: 2,847 travelers connected
                </Badge>
              </motion.div>

              {/* Animated heading */}
              <AnimatedHeading />

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-base text-muted-foreground leading-relaxed mb-5 max-w-lg"
              >
                Log your flights, share travel experiences by route, discover airport tips, and connect with fellow travelers worldwide.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex flex-col sm:flex-row gap-3"
              >
                <Dialog open={drawerOpen} onOpenChange={setDrawerOpen}>
                  <DialogTrigger asChild>
                    <Button
                      size="lg"
                      className="fly-btn-glossy px-8 h-12 text-base font-semibold"
                      data-testid="hero-checkin-btn"
                    >
                      <Plane className="mr-2 w-5 h-5" /> Log a Flight
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>Log Your Flight</DialogTitle>
                    </DialogHeader>
                    <div className="py-2" data-testid="flight-checkin-form">
                      <p className="text-sm text-muted-foreground mb-4">Enter your flight number to log it and explore the route community.</p>
                      <div className="flex gap-2">
                        <Input
                          placeholder="e.g. EK101, SV001, QR567"
                          value={flightNumber}
                          onChange={e => setFlightNumber(e.target.value.toUpperCase())}
                          onKeyDown={e => e.key === 'Enter' && handleCheckin()}
                          className="flex-1 text-base uppercase"
                          data-testid="flight-number-input"
                          autoFocus
                        />
                        <Button onClick={handleCheckin} disabled={checkingIn} className="fly-btn-primary" data-testid="flight-checkin-submit-button">
                          {checkingIn ? '...' : <ArrowRight className="w-4 h-4" />}
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-4">
                        {['EK101', 'SV001', 'QR567', 'TK1', 'BA003', 'QR001'].map(fn => (
                          <button key={fn} onClick={() => setFlightNumber(fn)} className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-[#FF4500]/10 hover:text-[#FF4500] transition-colors font-mono">{fn}</button>
                        ))}
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
                <Button size="lg" variant="outline" asChild className="h-12 text-base font-semibold border-2 hover:bg-foreground/5">
                  <Link to="/discover">Explore Routes <ChevronRight className="ml-1 w-4 h-4" /></Link>
                </Button>
              </motion.div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.55 }}
                className="flex gap-8 mt-6"
              >
                {[['164+', 'Airports'], ['75+', 'Blog Articles'], ['250+', 'Active Routes']].map(([n, l], i) => (
                  <motion.div
                    key={l}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.55 + i * 0.08 }}
                  >
                    <div className="text-2xl font-bold text-[#FF4500]" style={{ fontFamily: 'Space Grotesk' }}>{n}</div>
                    <div className="text-xs text-muted-foreground">{l}</div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right col — animated route card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.55, delay: 0.25 }}
              className="hidden lg:flex items-center justify-center"
            >
              <div className="w-full max-w-sm pb-2">
                <AnimatedRouteCard />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── ROUTE FINDER ──────────────────────────────────────────────── */}
      <section className="py-10 sm:py-16 bg-gradient-to-br from-[#0f172a] via-[#111827] to-[#1E3A8A]/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <div className="inline-flex items-center gap-2 bg-[#FF4500]/20 text-[#FF4500] border border-[#FF4500]/30 rounded-full px-3 py-1 text-xs font-semibold mb-4">
              <Navigation className="w-3.5 h-3.5" /> Route Finder
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3" style={{ fontFamily: 'Space Grotesk' }}>
              Find Your Route Community
            </h2>
            <p className="text-slate-400 max-w-md mx-auto text-sm sm:text-base">
              Enter any two airport codes — we'll find the community or create one for you.
            </p>
          </motion.div>

          {/* Finder input row */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="max-w-xl mx-auto"
          >
            <div className="flex items-end gap-2 sm:gap-3">
              {/* Origin */}
              <div className="flex-1">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5 ml-1">From</p>
                <Input
                  placeholder="DXB"
                  value={rfOrigin}
                  onChange={e => { setRfOrigin(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3)); setRfResult(null); setRfError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleRouteLookup()}
                  maxLength={3}
                  className="text-center text-xl font-bold uppercase tracking-[0.25em] h-14 bg-white/8 border-white/15 text-white placeholder:text-slate-600 focus:border-[#FF4500]/60 focus:bg-white/10"
                  data-testid="homepage-route-origin"
                />
              </div>

              {/* Divider */}
              <div className="pb-2 flex-shrink-0">
                <div className="w-8 h-8 rounded-full bg-[#FF4500]/20 border border-[#FF4500]/30 flex items-center justify-center">
                  <Plane className="w-4 h-4 text-[#FF4500]" />
                </div>
              </div>

              {/* Destination */}
              <div className="flex-1">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5 ml-1">To</p>
                <Input
                  placeholder="LHR"
                  value={rfDest}
                  onChange={e => { setRfDest(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3)); setRfResult(null); setRfError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleRouteLookup()}
                  maxLength={3}
                  className="text-center text-xl font-bold uppercase tracking-[0.25em] h-14 bg-white/8 border-white/15 text-white placeholder:text-slate-600 focus:border-[#FF4500]/60 focus:bg-white/10"
                  data-testid="homepage-route-dest"
                />
              </div>

              {/* Search button */}
              <div className="pb-0 flex-shrink-0">
                <Button
                  onClick={handleRouteLookup}
                  disabled={rfLoading || rfOrigin.length < 3 || rfDest.length < 3}
                  className="fly-btn-glossy h-14 px-5 text-sm font-bold"
                  data-testid="homepage-route-find-btn"
                >
                  {rfLoading
                    ? <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                    : <><Search className="w-4 h-4 mr-1.5" />Find</>
                  }
                </Button>
              </div>
            </div>

            {/* Quick examples */}
            <div className="flex flex-wrap gap-2 mt-3 justify-center">
              {[['DXB','LHR'],['RUH','DXB'],['DOH','JFK'],['SIN','SYD'],['IST','JFK']].map(([o,d]) => (
                <button
                  key={`${o}-${d}`}
                  onClick={() => { setRfOrigin(o); setRfDest(d); setRfResult(null); setRfError(''); }}
                  className="text-xs px-2.5 py-1 rounded-full text-slate-400 border border-white/10 hover:border-[#FF4500]/40 hover:text-[#FF4500] hover:bg-[#FF4500]/5 transition-all font-mono"
                >
                  {o} → {d}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Result card */}
          <AnimatePresence mode="wait">
            {rfError && (
              <motion.div
                key="error"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="max-w-xl mx-auto mt-6 p-4 rounded-2xl bg-red-500/10 border border-red-500/25 text-red-400 text-sm text-center"
              >
                {rfError}
              </motion.div>
            )}

            {rfResult && (
              <motion.div
                key="result"
                initial={{ opacity: 0, y: 16, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                className="max-w-xl mx-auto mt-6"
              >
                <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden backdrop-blur-sm">
                  {/* Route header */}
                  <div className="px-5 py-4 flex items-center gap-4 border-b border-white/10">
                    <div className="text-center flex-1">
                      <div className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>{rfResult.route?.origin_code}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{rfResult.origin_airport?.city || ''}</div>
                      {rfResult.origin_airport?.flag && <div className="text-lg mt-0.5">{rfResult.origin_airport.flag}</div>}
                    </div>

                    <div className="flex flex-col items-center gap-1 px-2">
                      <div className="flex items-center gap-1.5">
                        <div className="w-10 h-px bg-white/20" />
                        <Plane className="w-4 h-4 text-[#FF4500]" />
                        <div className="w-10 h-px bg-white/20" />
                      </div>
                      {rfResult.duration_hrs && (
                        <span className="text-[10px] text-slate-500 flex items-center gap-1">
                          <Clock className="w-2.5 h-2.5" />
                          {Math.floor(rfResult.duration_hrs)}h {Math.round((rfResult.duration_hrs % 1) * 60)}m
                        </span>
                      )}
                    </div>

                    <div className="text-center flex-1">
                      <div className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>{rfResult.route?.destination_code}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{rfResult.destination_airport?.city || ''}</div>
                      {rfResult.destination_airport?.flag && <div className="text-lg mt-0.5">{rfResult.destination_airport.flag}</div>}
                    </div>
                  </div>

                  {/* Stats row */}
                  <div className="grid grid-cols-2 divide-x divide-white/10 border-b border-white/10">
                    <div className="py-3 text-center">
                      <div className="text-xl font-bold text-[#FF4500]" style={{ fontFamily: 'Space Grotesk' }}>
                        {rfResult.route?.checkin_count || 0}
                      </div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-wide mt-0.5">Travelers</div>
                    </div>
                    <div className="py-3 text-center">
                      <div className="text-xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
                        {rfResult.route?.post_count || 0}
                      </div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-wide mt-0.5">Community Posts</div>
                    </div>
                  </div>

                  {/* New route banner */}
                  {rfResult.is_new && (
                    <div className="px-5 py-2.5 bg-[#FF4500]/10 border-b border-[#FF4500]/20 text-xs text-[#FF4500] flex items-center gap-2">
                      <Star className="w-3.5 h-3.5 flex-shrink-0" />
                      Be the first! This route community will be created when you log your flight.
                    </div>
                  )}

                  {/* Recent posts preview */}
                  {rfResult.posts_preview?.length > 0 && (
                    <div className="px-5 py-3 space-y-2 border-b border-white/10">
                      <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">Recent Community Posts</p>
                      {rfResult.posts_preview.slice(0, 2).map(post => (
                        <div key={post.post_id} className="text-xs text-slate-400 line-clamp-1 pl-2 border-l border-[#FF4500]/40">
                          <span className="text-slate-300 font-medium">{post.author_name}:</span> {post.content}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* CTA */}
                  <div className="px-5 py-4">
                    <Button
                      className="fly-btn-glossy w-full h-11 font-semibold"
                      onClick={() => navigate(`/route/${rfResult.route?.route_id}`)}
                      data-testid="homepage-route-go-btn"
                    >
                      <Plane className="w-4 h-4 mr-2" />
                      {rfResult.is_new ? 'Create Route Community' : 'Open Route Community'}
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* ── TRENDING ROUTES ────────────────────────────────────────────── */}
      {trendingRoutes.length > 0 && (
        <section className="py-5 border-b border-border">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
                <TrendingUp className="w-4 h-4 text-[#FF4500]" /> Trending Routes
              </h2>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/discover">View all <ChevronRight className="ml-1 w-3.5 h-3.5" /></Link>
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {trendingRoutes.map((route, i) => (
                <motion.div
                  key={route.route_id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Link to={`/route/${route.route_id}`}>
                    <Badge
                      className="py-1.5 px-3 text-sm font-mono cursor-pointer border bg-[#FF4500]/10 text-[#FF4500] border-[#FF4500]/30 hover:bg-[#FF4500] hover:text-white hover:border-[#FF4500] transition-all shadow-sm"
                    >
                      <Route className="w-3 h-3 mr-1.5" />
                      {route.route_id}
                      {route.checkin_count > 0 && (
                        <span className="ml-1.5 opacity-80 text-[10px]">· {route.checkin_count}</span>
                      )}
                    </Badge>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── LIVE FEED PREVIEW ─────────────────────────────────────────── */}
      <section className="py-8 sm:py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {/* Quiz CTA Banner — invite visitors to test their knowledge */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-6"
          >
            <QuizCTABanner variant="hero" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex items-center justify-between mb-5"
          >
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Happening Right Now</h2>
              <p className="text-muted-foreground mt-1">Live updates from airports worldwide</p>
            </div>
            <Button variant="outline" asChild size="sm">
              <Link to="/discover">See all <ChevronRight className="ml-1 w-4 h-4" /></Link>
            </Button>
          </motion.div>

          <div className="grid sm:grid-cols-2 gap-4">
            {recentPosts.slice(0, 4).map((post, i) => {
              const typeStyle = POST_TYPE_STYLES[post.post_type] || POST_TYPE_STYLES.general;
              const typeLabel = POST_TYPE_LABELS[post.post_type] || 'Update';
              return (
                <motion.div
                  key={post.post_id}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                >
                  <Card className="fly-card p-4 hover:shadow-lg transition-shadow" data-testid="post-card">
                    <div className="flex items-start gap-3">
                      <img src={post.author_avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${post.author_id}`} alt="" className="w-9 h-9 rounded-full flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-medium">{post.author_name}</span>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${typeStyle}`}>{typeLabel}</span>
                          {post.airport_code && (
                            <span className="flex items-center gap-0.5 text-xs text-muted-foreground ml-auto">
                              <MapPin className="w-3 h-3" />{post.airport_code}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-foreground leading-relaxed line-clamp-2">{post.content}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                          <span>{(post.reactions?.like || 0) + (post.reactions?.love || 0)} reactions</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── FEATURED AIRPORTS ─────────────────────────────────────────── */}
      <section className="py-8 sm:py-12 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-7"
          >
            <h2 className="text-2xl sm:text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Explore Top Airports</h2>
            <p className="text-muted-foreground">Join thousands of travelers at these major hubs</p>
          </motion.div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            {airports.map((apt, i) => (
              <motion.div
                key={apt.iata_code}
                initial={{ opacity: 0, scale: 0.92 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
              >
                <Link to={`/airport/${apt.iata_code}`} className="block" data-testid="airport-card">
                  <Card className="fly-card p-3 text-center hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer group">
                    <div className="w-10 h-10 mx-auto mb-2 rounded-xl overflow-hidden ring-2 ring-border group-hover:ring-[#FF4500]/30 transition-all">
                      <img src={apt.image_url || 'https://images.unsplash.com/photo-1530521954074-e64f6810b32d?w=100&q=80'} alt={apt.city} className="w-full h-full object-cover" />
                    </div>
                    <div className="text-sm font-bold text-[#FF4500]" style={{ fontFamily: 'Space Grotesk' }}>{apt.iata_code}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{apt.city}</div>
                    {apt.post_count > 0 && <div className="text-[10px] font-medium text-[#10B981] mt-0.5">{apt.post_count} posts</div>}
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button variant="outline" asChild>
              <Link to="/discover">View All 164 Airports <ChevronRight className="ml-1 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ──────────────────────────────────────────────── */}
      <section className="py-8 sm:py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl sm:text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>How It Works</h2>
            <p className="text-muted-foreground">Three steps to join your travel community</p>
          </motion.div>
          <div className="grid sm:grid-cols-3 gap-8">
            {[
              { icon: Plane, step: '01', title: 'Log Your Flight', desc: 'Enter your flight number and instantly join the community for your exact route. See what other travelers say about seats, food, and service.' },
              { icon: MessageSquare, step: '02', title: 'Share Experiences', desc: 'Post seat tips, food recommendations, and real experiences. Help the next traveler on your route have a better journey.' },
              { icon: Users, step: '03', title: 'Build Your Network', desc: 'Earn badges, climb the leaderboard, and connect with frequent travelers who fly your routes.' },
            ].map(({ icon: Icon, step, title, desc }, i) => (
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#FF4500]/10 flex items-center justify-center">
                  <Icon className="w-8 h-8 text-[#FF4500]" />
                </div>
                <div className="text-5xl font-bold text-[#FF4500]/15 mb-2" style={{ fontFamily: 'Space Grotesk' }}>{step}</div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Space Grotesk' }}>{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ──────────────────────────────────────────────────── */}
      <section className="py-8 sm:py-12 bg-muted/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl sm:text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Everything You Need</h2>
            <p className="text-muted-foreground">Built for modern travelers</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Zap,          title: 'Real-time Alerts',    desc: 'Gate changes, delays, and crowd levels updated by the community instantly.',                                                                    color: '#FF4500' },
              { icon: MessageSquare,title: 'Route Communities',    desc: 'Share experiences, seat tips, and photos for every flight route. Discover what travelers say about your upcoming journey.',                    color: '#1E3A8A' },
              { icon: Globe,         title: 'Destination Guides',  desc: 'Get transport, attraction, and food tips for every destination city — automatically shown on every route page.',                               color: '#10B981' },
              { icon: MapPin,        title: 'Airport Guides',      desc: 'Community-written lounge tips, food spots, and hidden airport gems written by frequent flyers.',                                                color: '#FF4500' },
              { icon: Shield,        title: 'Safe & Moderated',    desc: 'Content moderation keeps the community helpful and respectful. Report issues in one tap.',                                                     color: '#1E3A8A' },
              { icon: Star,          title: 'Earn Travel Badges',  desc: 'Collect badges like Frequent Flyer, Jet Setter, and Sky Legend as you log flights and contribute.',                                            color: '#D97706' },
            ].map(({ icon: Icon, title, desc, color }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
              >
                <Card className="fly-card p-5 h-full hover:shadow-lg transition-shadow group">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 transition-transform group-hover:scale-110" style={{ background: `${color}18` }}>
                    <Icon className="w-5 h-5" style={{ color }} />
                  </div>
                  <h3 className="font-semibold mb-1.5" style={{ fontFamily: 'Space Grotesk' }}>{title}</h3>
                  <p className="text-sm text-muted-foreground">{desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ──────────────────────────────────────────────── */}
      <section className="py-8 sm:py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl sm:text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>What Travelers Say</h2>
          </motion.div>
          <div className="grid sm:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="fly-card p-5 h-full">
                  <div className="flex gap-0.5 mb-3">
                    {[...Array(t.rating)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#FF4500] text-[#FF4500]" />)}
                  </div>
                  <p className="text-sm text-foreground leading-relaxed mb-4">"{t.text}"</p>
                  <div className="flex items-center gap-2 mt-auto">
                    <img src={t.avatar} alt={t.name} className="w-9 h-9 rounded-full" />
                    <div>
                      <div className="text-sm font-semibold">{t.name}</div>
                      <div className="text-xs text-muted-foreground">{t.role} · {t.airport}</div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────── */}
      <section className="py-12 sm:py-16 bg-gradient-to-br from-[#111827] via-[#1E3A8A]/80 to-[#111827] text-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk' }}>Ready to Join Your Community?</h2>
            <p className="text-gray-300 text-lg mb-8">Log your first flight and connect with travelers on your route — free forever.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" className="fly-btn-primary px-10" onClick={() => setShowAuth(true)} data-testid="cta-join-btn">
                <Plane className="mr-2 w-5 h-5" /> Join Free Today
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" asChild>
                <Link to="/how-it-works">Learn More</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <AuthModal open={showAuth} onOpenChange={setShowAuth} />
    </PageLayout>
  );
}
